import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CuttingDraft } from '@pomoshnik/shared';
import { CuttingLayout } from './CuttingLayout';
import { CuttingTabBar } from './CuttingTabBar';
import { ParamsTab } from './ParamsTab';
import { PartsTab } from './PartsTab';
import { OffcutsTab } from './OffcutsTab';
import { ResultsTab } from './ResultsTab';
import { CutMap } from './CutMap';
import { CutPrintMap } from './CutPrintMap';
import { CutPrintDetails } from './CutPrintDetails';
import { CutOffcutList } from './CutOffcutList';
import { ResultActions } from './ResultActions';
import { CUTTING_TABS, type CuttingTabId } from './cuttingTabs';
import { useCuttingStore } from './state/useCuttingStore';
import { useAutosave } from './state/useAutosave';
import { DraftsDialog } from './dialogs/DraftsDialog';
import { ImportFormatDialog } from './dialogs/ImportFormatDialog';

/**
 * Block 14 «Раскрой» — Экран 2.14.2 «Рабочий экран» (`project.html`). PR #2-UI + #4-SHELL.
 *
 * Горизонтальный TabBar (6 вкладок, ADR-0063), живой индикатор автосейва (CUT-1.6).
 * Меню (5 пунктов, вербатим прототипа project.html:55–79): Новый проект → `/cutting`,
 * Открыть проект → `/cutting/history`, Открыть черновик → модалка `DraftsDialog`,
 * Импорт → модалка `ImportFormatDialog`, Выход → `/cutting`. Прочие вкладки — заглушка.
 */

type DialogId = 'drafts' | 'import' | null;

interface MenuItem {
  id: string;
  icon: string;
  text: string;
  onClick: () => void;
}

export function CuttingProject() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<CuttingTabId>('menu');
  const [dialog, setDialog] = useState<DialogId>(null);
  const autosaveLabel = useAutosave();
  const adoptDraft = useCuttingStore((s) => s.adoptDraft);
  const activeLabel = CUTTING_TABS.find((t) => t.id === activeTab)?.label ?? 'Меню';

  const menuItems: MenuItem[] = [
    { id: 'new', icon: '📁', text: 'Новый проект', onClick: () => navigate('/cutting') },
    { id: 'open', icon: '📂', text: 'Открыть проект', onClick: () => navigate('/cutting/history') },
    { id: 'draft', icon: '📁', text: 'Открыть черновик', onClick: () => setDialog('drafts') },
    { id: 'import', icon: '📥', text: 'Импорт', onClick: () => setDialog('import') },
    { id: 'exit', icon: '🚪', text: 'Выход', onClick: () => navigate('/cutting') },
  ];

  const handleSelectDraft = (draft: CuttingDraft) => {
    adoptDraft(draft);
    setDialog(null);
    setActiveTab('details'); // прототип loadDraft → клик по вкладке «Детали и кромки»
  };

  return (
    <CuttingLayout
      title={activeLabel}
      autosaveLabel={autosaveLabel}
      tabBar={<CuttingTabBar activeTab={activeTab} onSelect={setActiveTab} />}
      mobileHeaderless
    >
      {activeTab === 'menu' ? (
        <div className="sc-menu-list">
          {menuItems.map((item) => (
            <button key={item.id} type="button" className="sc-menu-item" onClick={item.onClick}>
              <div className="sc-menu-icon">{item.icon}</div>
              <div className="sc-menu-text">{item.text}</div>
              <div className="sc-menu-arrow">›</div>
            </button>
          ))}
        </div>
      ) : activeTab === 'settings' ? (
        <ParamsTab />
      ) : activeTab === 'details' ? (
        <PartsTab onCutComplete={() => setActiveTab('result')} />
      ) : activeTab === 'leftovers' ? (
        <OffcutsTab />
      ) : activeTab === 'result' ? (
        <>
          {/* DOM-порядок: КАРТА первой (#170-нюансы #2) — чтобы в печати карта
              попадала на СТРАНИЦУ 1 без ведущего пустого листа (фрагментация Chrome
              идёт по DOM-порядку; break-after карты → стр.2 = метрики+детали).
              На ЭКРАНЕ исходный порядок (метрики → карта) восстанавливается через
              flex `order` (`.cutting-print-region` flex-column, cutting-theme.css).
              CutMap сам возвращает null без success-результата — без дубля stub.
              ResultActions (Сохранить проект / Печать / Остатки, PR #24-B + #25) —
              вне региона (кнопки не печатаются). */}
          <div className="cutting-print-region">
            <CutMap />
            {/* Печать ВСЕХ листов (PR block-14-print-all-sheets): экранный CutMap —
                один лист (пейджер), print-only CutPrintMap — каждый лист на своей
                странице. В печати экранный CutMap скрыт
                (`.result-canvas-wrap:not(.cut-print-sheet)`), на экране CutPrintMap
                скрыт (`.cut-print-only`). */}
            <CutPrintMap />
            <ResultsTab />
            {/* Список деталей — print-only (PR #25 #168): на экране скрыт, в печать
                идёт вместе с метриками + картой (спецификация для цеха). */}
            <CutPrintDetails />
            {/* Список остатков — экран (под картой, order 4) + печать (после деталей).
                Видна на экране (не print-only). Пусто → null. */}
            <CutOffcutList />
          </div>
          <ResultActions />
        </>
      ) : (
        <div className="sc-stub-card" aria-live="polite">
          <h2 className="sc-stub-title">{activeLabel}</h2>
          <p className="sc-stub-text">Раздел в разработке.</p>
        </div>
      )}

      {dialog === 'drafts' && (
        <DraftsDialog onClose={() => setDialog(null)} onSelect={handleSelectDraft} />
      )}
      {dialog === 'import' && <ImportFormatDialog onClose={() => setDialog(null)} />}
    </CuttingLayout>
  );
}
